import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { subscriptionCalendarNames } from "@/lib/subscriptionCalendars";
import type { CalendarScopedSubscription } from "@/lib/subscriptionCalendars";
import { attachCalendarScopes } from "@/services/subscriptionCalendarsService";
import { listCalendars } from "@/services/edgeFunctionWrappers/calendarsWrapper";

/**
 * Libellé du planning associé à chaque abonnement, pour l'espace adhérent.
 *
 * Renvoie `null` pour un abonnement dont le planning ne veut rien dire —
 * concrètement quand la salle n'a aucun calendrier ou qu'aucun abonnement
 * n'est typé. L'appelant n'affiche alors AUCUNE mention de planning, ce qui
 * laisse l'écran des salles sans multi-planning strictement identique.
 *
 * Aucune requête n'est émise tant qu'aucun abonnement ne porte de
 * `subscription_type_id`.
 */
export function useSubscriptionCalendarLabels(
  subscriptions: CalendarScopedSubscription[],
  gymId: string | null | undefined
) {
  const { t } = useTranslation(["subscriptions"]);
  const [labelById, setLabelById] = useState<Record<string, string | null>>({});

  // Clé stable : on ne relance la résolution que si la composition
  // (abonnement, type) change réellement.
  const signature = subscriptions
    .map((s) => `${s.id ?? ""}:${s.subscription_type_id ?? ""}`)
    .join("|");

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      const typed = subscriptions.some((s) => !!s.subscription_type_id);
      if (!typed || !gymId) {
        if (!cancelled) setLabelById({});
        return;
      }

      try {
        const [scoped, calendars] = await Promise.all([
          attachCalendarScopes(subscriptions, gymId),
          listCalendars(gymId, true),
        ]);
        if (cancelled) return;

        // Salle sans calendrier configuré : rien à afficher.
        if (calendars.length === 0) {
          setLabelById({});
          return;
        }

        const nameById = Object.fromEntries(calendars.map((c) => [c.id, c.name]));
        const next: Record<string, string | null> = {};
        for (const sub of scoped) {
          if (!sub.id) continue;
          const names = subscriptionCalendarNames(sub, nameById);
          next[sub.id] = names.length > 0 ? names.join(", ") : t("calendars.allSchedules");
        }
        setLabelById(next);
      } catch (error) {
        // Le libellé est purement informatif : son absence ne doit jamais
        // empêcher l'affichage des abonnements.
        console.error("[useSubscriptionCalendarLabels] Résolution des plannings en échec:", error);
        if (!cancelled) setLabelById({});
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, gymId, t]);

  return {
    /** `null`/absent = ne rien afficher pour cet abonnement. */
    calendarLabelFor: (subscriptionId: string | null | undefined): string | null =>
      (subscriptionId && labelById[subscriptionId]) || null,
    hasCalendarLabels: Object.keys(labelById).length > 0,
  };
}
